// Client-side view model for conversation turns, including optimistic and streaming ones.
import type { ConversationRole, KeenOutcome, SseDoneStatus, TurnPurpose, TurnRating, TurnWithParts } from '@jabberr/core';

export type ViewStatus = 'pending' | 'streaming' | 'completed' | 'cancelled' | 'failed' | 'unavailable';

/** turns.cancel_reason when the prompt could not fit the model window; the conversation is full. */
export const CONTEXT_TOO_LONG = 'context_too_long';

/** A tool call the reply made, as shown under the thinking row: "Searched for X · 8 results". */
export interface ViewTool {
  callId: string;
  name: string;
  label: string;
  status: 'running' | 'done' | 'error';
  outcome?: string;
}

export interface ViewTurn {
  /** Stable React key: the server id once known, otherwise "client:<clientTurnId>". */
  key: string;
  id: string | null;
  clientTurnId: string | null;
  kind: 'user' | 'assistant' | 'system' | 'summary';
  status: ViewStatus;
  answeredRole?: ConversationRole | null;
  text: string;
  reasoning: string;
  tools: ViewTool[];
  /** A document kind, 'saved' when a person kept the reply on Outputs, else null. */
  purpose: TurnPurpose | null;
  /** The reader's thumbs on a completed reply. */
  rating: TurnRating | null;
  /** Resolved @-mentions of a user turn: member user ids, plus 'assistant' for @jabber. */
  mentions: string[];
  /** Keen mode: the decider's probability on a user turn; null when no decision ran. */
  interjectP: number | null;
  /** Keen mode: persisted decision result, including silence without a probability. */
  keenOutcome?: KeenOutcome | null;
  /** Display name of the person who made a user turn (team threads with more than one speaker). */
  actorName?: string | null;
  seq: number | null;
  error?: string;
  /** For a failed user turn: whether the server persisted it (retry must use a fresh clientTurnId). */
  persisted?: boolean;
  /** The reply stopped at the engine's length limit (finish_reason length). */
  truncated?: boolean;
  cancelReason?: string | null;
  createdAt: string;
}

function partText(turn: TurnWithParts, kind: 'text' | 'reasoning'): string {
  return turn.parts
    .filter((p) => p.kind === kind)
    .sort((a, b) => a.idx - b.idx)
    .map((p) => (typeof p.content['text'] === 'string' ? (p.content['text'] as string) : ''))
    .join('');
}

function toolsOf(turn: TurnWithParts): ViewTool[] {
  const results = new Map<string, { error?: string }>();
  for (const p of turn.parts) {
    if (p.kind === 'tool_result' && typeof p.content['callId'] === 'string') {
      results.set(p.content['callId'] as string, typeof p.content['error'] === 'string' ? { error: p.content['error'] as string } : {});
    }
  }
  return turn.parts
    .filter((p) => p.kind === 'tool_call')
    .sort((a, b) => a.idx - b.idx)
    .map((p) => {
      const callId = String(p.content['callId'] ?? '');
      const result = results.get(callId);
      return {
        callId,
        name: String(p.content['name'] ?? ''),
        label: String(p.content['label'] ?? ''),
        status: result?.error ? 'error' : 'done',
        ...(result?.error ? { outcome: result.error } : {}),
      };
    });
}

export function fromServerTurn(turn: TurnWithParts): ViewTurn {
  const truncated = turn.parts.some((p) => p.kind === 'text' && p.content['truncated'] === true);
  return {
    key: turn.id,
    id: turn.id,
    clientTurnId: turn.clientTurnId,
    kind: turn.kind,
    answeredRole: turn.answeredRole,
    status: turn.status,
    text: partText(turn, 'text'),
    reasoning: partText(turn, 'reasoning'),
    tools: toolsOf(turn),
    purpose: turn.purpose,
    rating: turn.rating,
    mentions: turn.mentions,
    interjectP: turn.interjectP,
    keenOutcome: turn.keenOutcome ?? null,
    actorName: turn.actorName,
    seq: turn.seq,
    persisted: true,
    ...(truncated ? { truncated: true } : {}),
    cancelReason: turn.cancelReason,
    createdAt: turn.createdAt,
  };
}

/** True when the conversation's latest turn says the prompt no longer fits: the web offers a new conversation instead of Retry. */
export function isContextFull(turns: ViewTurn[]): boolean {
  const last = [...turns].reverse().find((t) => t.kind === 'assistant' || t.kind === 'user');
  return !!last && last.kind === 'assistant' && last.status === 'failed' && last.cancelReason === CONTEXT_TOO_LONG;
}

/** The latest summary checkpoint, shown as a collapsed "Earlier in this conversation" row. */
export function latestSummary(turns: ViewTurn[]): ViewTurn | undefined {
  return [...turns].reverse().find((t) => t.kind === 'summary' && t.status === 'completed');
}

export function fromServerTurns(turns: TurnWithParts[]): ViewTurn[] {
  return [...turns].sort((a, b) => a.seq - b.seq).map(fromServerTurn);
}

/** Keen's latest silent turn can be answered by a person, including decisions that returned no probability. */
export function canReplyAnyway(turn: ViewTurn, later: ViewTurn[]): boolean {
  if (turn.kind !== 'user' || !turn.id) return false;
  const silentOutcome = turn.keenOutcome != null && turn.keenOutcome !== 'spoke' && turn.keenOutcome !== 'superseded';
  return (silentOutcome || turn.interjectP !== null) && !later.some((x) => x.kind === 'user' || (x.kind === 'assistant' && x.status === 'completed'));
}

export function optimisticUserTurn(clientTurnId: string, content: string): ViewTurn {
  return {
    key: `client:${clientTurnId}`,
    id: null,
    clientTurnId,
    kind: 'user',
    status: 'pending',
    text: content,
    reasoning: '',
    tools: [],
    purpose: null,
    rating: null,
    mentions: [],
    interjectP: null,
    seq: null,
    persisted: false,
    createdAt: new Date().toISOString(),
  };
}

export function placeholderAssistantTurn(clientTurnId: string): ViewTurn {
  return {
    key: `client:${clientTurnId}:assistant`,
    id: null,
    clientTurnId: null,
    kind: 'assistant',
    status: 'streaming',
    text: '',
    reasoning: '',
    tools: [],
    purpose: null,
    rating: null,
    mentions: [],
    interjectP: null,
    seq: null,
    persisted: false,
    createdAt: new Date().toISOString(),
  };
}

export function statusFromOutcome(outcome: SseDoneStatus): ViewStatus {
  switch (outcome) {
    case 'completed':
      return 'completed';
    case 'cancelled':
      return 'cancelled';
    case 'unavailable':
      return 'unavailable';
    case 'error':
    case 'context_too_long':
      return 'failed';
  }
}

/**
 * Merge freshly loaded server turns into the local list, keeping local optimistic
 * turns that the server has not returned yet (matched by clientTurnId or id).
 */
export function mergeTurns(local: ViewTurn[], server: ViewTurn[]): ViewTurn[] {
  const serverIds = new Set(server.map((t) => t.id));
  const serverClientIds = new Set(server.map((t) => t.clientTurnId).filter((x): x is string => !!x));
  const keep = local.filter((t) => {
    if (t.id && serverIds.has(t.id)) return false;
    if (t.clientTurnId && serverClientIds.has(t.clientTurnId)) return false;
    return true;
  });
  return [...server, ...keep];
}
